import { Switch, Text } from "@gravity-ui/uikit";
import { lazy, Suspense, type Ref, useEffect, useMemo, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";

import type { ExportTaskFormValues } from "../../ExportTasksForm.types";
import {
  TemplateChipEditor,
  type TemplateChipEditorHandle,
} from "./TemplateChipEditor";
import css from "./TemplateEditor.module.scss";
import type { TemplateField } from "./TemplateEditor.types";
import {
  applySuggestion,
  buildIsValid,
  buildParseErrorRanges,
  buildUnknownVariableRanges,
  cleanTemplateOptions,
} from "./TemplateEditor.utils";
import { TemplateOptionsFields } from "./TemplateOptionsFields";
import { TemplatePreviewPanel } from "./TemplatePreviewPanel";
import {
  TEMPLATE_MAX_LENGTH,
  type PreviewSample,
} from "./TemplatesForm.constants";
import type {
  TemplateApiError,
  TemplateOptionsDto,
  TemplatePreviewResponse,
  TemplateVariablesResponse,
} from "./TemplatesForm.types";
import { UnknownVariableList } from "./UnknownVariableList";
import { useKnownPaths } from "./useKnownPaths";
import { useTemplateSwitch } from "./useTemplateSwitch";

export type { TemplateField } from "./TemplateEditor.types";

// Lazy-load: TipTap+ProseMirror (~80КБ gz) попадают в основной чанк
// ExportPage, хотя rich-режим используется только для Avito-description (≈10%
// сессий). React.lazy + Suspense выносит код в отдельный chunk, который
// тянется только когда format=Avito и юзер дошёл до description-вкладки.
const RichTemplateEditor = lazy(() =>
  import("src/components/RichTemplateEditor").then((m) => ({
    default: m.RichTemplateEditor,
  })),
);

interface TemplateEditorProps {
  field: TemplateField;
  title: string;
  description: string;
  applyOnlyIfEmptyLabel: string;
  defaultMaxLength?: number;
  variables: TemplateVariablesResponse | null;
  previewData: TemplatePreviewResponse | null;
  previewLoading: boolean;
  previewError: TemplateApiError | null;
  previewProducts: PreviewSample[];
  warning?: string;
  exposeInsert?: (insertFn: (placeholder: string) => void) => void;
  // Включает WYSIWYG (TipTap) вместо textarea-чип-редактора. Используется
  // для description у Avito: формат разрешает HTML (p, br, strong, em, ul, ol, li).
  richText?: boolean;
}

export const TemplateEditor = ({
  field,
  title,
  description,
  applyOnlyIfEmptyLabel,
  defaultMaxLength,
  variables,
  previewData,
  previewLoading,
  previewError,
  previewProducts,
  warning,
  exposeInsert,
  richText = false,
}: TemplateEditorProps) => {
  const { control } = useFormContext<ExportTaskFormValues>();
  const editorRef = useRef<TemplateChipEditorHandle | null>(null);
  const { enabled, formFieldName, turnOn, turnOff } = useTemplateSwitch(field);

  const knownPaths = useKnownPaths(variables);
  const isValid = useMemo(() => buildIsValid(knownPaths), [knownPaths]);

  // Поднимаем insertAtCursor наверх, чтобы общий сайдбар вставлял в активный
  // редактор. Подписка активна только пока editor смонтирован и enabled.
  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler, react-you-might-not-need-an-effect/no-pass-data-to-parent -- регистрируем императивный insert-handle в родителе (общий сайдбар вставляет в активный редактор) */
  useEffect(() => {
    if (!enabled || !exposeInsert) return;
    exposeInsert((placeholder) =>
      editorRef.current?.insertAtCursor(placeholder),
    );
  }, [enabled, exposeInsert]);
  /* eslint-enable react-you-might-not-need-an-effect/no-event-handler, react-you-might-not-need-an-effect/no-pass-data-to-parent */

  return (
    <div className={css.root}>
      <div className={css.header}>
        <div className={css.titleBlock}>
          <Text variant="subheader-2">{title}</Text>
          <Text variant="body-2" color="secondary">
            {description}
          </Text>
        </div>
        <Switch
          size="m"
          checked={enabled}
          onUpdate={(next) =>
            next ? turnOn(() => editorRef.current?.focus()) : turnOff()
          }
          aria-label={`Включить шаблон ${title}`}
        >
          {enabled ? "Включен" : "Выключен"}
        </Switch>
      </div>

      {enabled && (
        <Controller
          name={formFieldName}
          control={control}
          render={({ field: rhfField }) => (
            <TemplateEditorBody
              field={field}
              rhfField={rhfField}
              defaultMaxLength={defaultMaxLength}
              isValid={isValid}
              previewError={previewError}
              previewData={previewData}
              previewLoading={previewLoading}
              previewProducts={previewProducts}
              warning={warning}
              applyOnlyIfEmptyLabel={applyOnlyIfEmptyLabel}
              editorRef={editorRef}
              richText={richText}
            />
          )}
        />
      )}
    </div>
  );
};

interface TemplateEditorBodyProps {
  field: TemplateField;
  rhfField: {
    value?: TemplateOptionsDto;
    onChange: (value: TemplateOptionsDto) => void;
    onBlur: () => void;
  };
  defaultMaxLength?: number;
  isValid: (raw: string) => boolean;
  previewError: TemplateApiError | null;
  previewData: TemplatePreviewResponse | null;
  previewLoading: boolean;
  previewProducts: PreviewSample[];
  warning?: string;
  applyOnlyIfEmptyLabel: string;
  editorRef: Ref<TemplateChipEditorHandle>;
  richText: boolean;
}

// Внутренний body — render-prop Controller'a. Вынесён в отдельный компонент,
// чтобы основной TemplateEditor читался как короткая оркестрация без 100+
// строк JSX внутри callback'a.
const TemplateEditorBody = ({
  field,
  rhfField,
  defaultMaxLength,
  isValid,
  previewError,
  previewData,
  previewLoading,
  previewProducts,
  warning,
  applyOnlyIfEmptyLabel,
  editorRef,
  richText,
}: TemplateEditorBodyProps) => {
  const editorValue = rhfField.value?.template ?? "";
  const fallbackValue = rhfField.value?.fallback ?? "";
  const applyOnlyIfEmpty = rhfField.value?.applyOnlyIfEmpty ?? false;
  const maxLength = rhfField.value?.maxLength ?? defaultMaxLength ?? undefined;

  const parseErrorRanges = useMemo(
    () => buildParseErrorRanges(previewError),
    [previewError],
  );
  const unknownVariableRanges = useMemo(
    () =>
      previewError?.kind === "unknown"
        ? buildUnknownVariableRanges(editorValue, previewError.unknown)
        : [],
    [previewError, editorValue],
  );
  const highlightRanges = useMemo(
    () => [...parseErrorRanges, ...unknownVariableRanges],
    [parseErrorRanges, unknownVariableRanges],
  );

  const patch = (next: Partial<TemplateOptionsDto>) => {
    const merged: TemplateOptionsDto = {
      template: editorValue,
      ...rhfField.value,
      ...next,
    };
    rhfField.onChange(cleanTemplateOptions(merged));
  };

  return (
    <div className={css.body}>
      <label className={css.fieldLabel} htmlFor={`tpl-${field}`}>
        Шаблон
      </label>
      {richText ? (
        // В rich-режиме inline-подсветка `invalidRanges` теряет смысл: offsets
        // считаются на template-строке, а в редакторе мы показываем HTML.
        // UnknownVariableList ниже всё равно отрисует suggestion-кнопки.
        // Suspense fallback — skeleton-textarea того же размера, чтобы layout
        // не прыгал, пока подгружается chunk с TipTap.
        <Suspense
          fallback={
            <div className={css.editorSkeleton} aria-busy="true">
              Загружаем редактор…
            </div>
          }
        >
          <RichTemplateEditor
            ref={editorRef}
            id={`tpl-${field}`}
            value={editorValue}
            onChange={(next) => patch({ template: next })}
            onBlur={rhfField.onBlur}
            placeholder="Например: <p>{{vendor}} {{title}}</p>"
            ariaInvalid={!!previewError}
            ariaDescribedBy={`tpl-${field}-error`}
          />
        </Suspense>
      ) : (
        <TemplateChipEditor
          ref={editorRef}
          id={`tpl-${field}`}
          value={editorValue}
          onChange={(next) => patch({ template: next })}
          onBlur={rhfField.onBlur}
          placeholder="Например: {{vendor}} {{seriesName}} {{title}}"
          isValid={isValid}
          invalidRanges={highlightRanges}
          ariaInvalid={!!previewError}
          ariaDescribedBy={`tpl-${field}-error`}
          maxLength={TEMPLATE_MAX_LENGTH}
          rows={3}
        />
      )}

      {previewError?.kind === "parse" && (
        <div id={`tpl-${field}-error`} className={css.parseError} role="alert">
          Ошибка синтаксиса: {previewError.detail}
        </div>
      )}

      <UnknownVariableList
        id={`tpl-${field}-error`}
        ranges={unknownVariableRanges}
        onApplySuggestion={(path, suggestion) =>
          patch({ template: applySuggestion(editorValue, path, suggestion) })
        }
      />

      <TemplateOptionsFields
        idPrefix={`tpl-${field}`}
        fallback={fallbackValue}
        maxLength={maxLength}
        applyOnlyIfEmpty={applyOnlyIfEmpty}
        applyOnlyIfEmptyLabel={applyOnlyIfEmptyLabel}
        onUpdateFallback={(next) => patch({ fallback: next })}
        onUpdateMaxLength={(next) => patch({ maxLength: next })}
        onUpdateApplyOnlyIfEmpty={(next) => patch({ applyOnlyIfEmpty: next })}
      />

      <TemplatePreviewPanel
        field={field}
        data={previewData}
        isLoading={previewLoading}
        error={previewError}
        products={previewProducts}
        hasTemplate={editorValue.trim().length > 0}
        warning={warning}
      />
    </div>
  );
};
